import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function LocationInput({
  value,
  onChange,
  recents,
  onRemoveRecent,
  darkMode,
  audioMode,
  placeholder,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return recents;
    return recents.filter(
      (n) => n.toLowerCase().includes(q) && n.toLowerCase() !== q
    );
  }, [recents, value]);

  const showDropdown = open && filtered.length > 0;
  const hasRecents = recents.length > 0;

  const selectRecent = (name) => {
    onChange(name);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={`w-full p-3 ${hasRecents ? 'pr-10' : ''} border ${
          darkMode
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
        } ${
          audioMode ? 'eight-bit-button' : 'rounded'
        } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
      />
      {hasRecents && (
        <button
          type="button"
          aria-label={t('setup.location.toggleRecents')}
          onClick={() => {
            const next = !open;
            setOpen(next);
            if (next) inputRef.current?.focus();
          }}
          className={`absolute inset-y-0 right-0 flex items-center px-3 ${
            darkMode
              ? 'text-gray-300 hover:text-white'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ChevronDown
            size={18}
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      {showDropdown && (
        <div
          className={`absolute z-20 mt-1 w-full max-h-60 overflow-auto border shadow-lg ${
            audioMode
              ? 'eight-bit-panel bg-white text-black'
              : darkMode
                ? 'rounded-md bg-gray-800 border-gray-700'
                : 'rounded-md bg-white border-gray-200'
          }`}
          role="listbox"
        >
          <div
            className={`px-3 py-1.5 text-xs uppercase tracking-wide ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            } ${audioMode ? 'eight-bit-text' : ''}`}
          >
            {t('setup.location.recents')}
          </div>
          {filtered.map((name) => (
            <div
              key={name}
              role="option"
              aria-selected={false}
              className={`flex items-center justify-between gap-2 px-3 py-2 cursor-pointer ${
                darkMode
                  ? 'hover:bg-gray-700 text-white'
                  : 'hover:bg-gray-100 text-gray-900'
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectRecent(name)}
            >
              <span
                className={`flex-1 truncate text-sm ${
                  audioMode ? 'eight-bit-text' : ''
                }`}
              >
                {name}
              </span>
              <button
                type="button"
                aria-label={t('setup.location.removeRecent', { name })}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveRecent(name);
                }}
                className={`p-1 ${
                  darkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-400 hover:text-gray-700'
                }`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
